import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getPublishJobs, getJobOpeningLimit } from "../Services/LiveJobService";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import NormalAlert from "../../../../Alerts/NormalAlert";
import { Funnel, MoreVertical, Pencil, UserPlus } from "lucide-react";
import ColumnFilterModal from "../../../CommonModals/ColumnFilterModal";
import Pagination from "../../../CommonModals/Pagination";

const LiveJobs = () => {
	const navigate = useNavigate();
	const menuRef = useRef(null);

	//  DATA STATE
	const [jobs, setJobs] = useState([]);

	//  UI STATE
	const [loading, setLoading] = useState(true);
	const [alert, setAlert] = useState(null);
	const [openMenu, setOpenMenu] = useState(null);

	//  SEARCH & FILTER
	const [searchTerm, setSearchTerm] = useState("");
	const [activeFilter, setActiveFilter] = useState(null);
	const [filterSearch, setFilterSearch] = useState("");

	const [filters, setFilters] = useState({
		jobTitle: [],
	});

	//  PAGINATION
	const [currentPage, setCurrentPage] = useState(1);
	const [jobsPerPage, setJobsPerPage] = useState(10);

	//  FETCH JOBS
	useEffect(() => {
		const fetchJobs = async () => {
			try {
				const data = await getPublishJobs();
				const publishedJobs = data?.data?.publishedJobs;

				if (Array.isArray(publishedJobs)) {
					setJobs(publishedJobs);
				} else {
					setJobs([]);
				}
			} catch (err) {
				setAlert({
					type: "fail",
					message:
						err?.response?.data?.message ||
						err?.message ||
						"Failed to fetch published jobs",
				});
				setJobs([]);
			} finally {
				setLoading(false);
			}
		};

		fetchJobs();
	}, []);

	//  OUTSIDE CLICK
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!event.target.closest(".action-menu")) {
				setOpenMenu(null);
			}
		};

		document.addEventListener("click", handleClickOutside);

		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, []);

	//  FILTER OPTIONS
	const filterOptions = {
		jobTitle: [...new Set(jobs.map((j) => j.jobTitle))],
	};

	//  FILTERED JOBS
	const filteredJobs = jobs.filter((job) => {
		const selectedTitles = Array.isArray(filters.jobTitle)
			? filters.jobTitle
			: [];

		const matchesFilter =
			selectedTitles.length === 0 || selectedTitles.includes(job.jobTitle);

		const matchesSearch = [
			job.jobTitle,
			job.department,
			job.location,
			job.grade,
			job.function,
		].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()));

		return matchesFilter && matchesSearch;
	});

	//  PAGINATION LOGIC
	const indexOfLast = currentPage * jobsPerPage;
	const indexOfFirst = indexOfLast - jobsPerPage;
	const currentJobs = filteredJobs.slice(indexOfFirst, indexOfLast);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchTerm, filters]);

	//  MENU OUTSIDE CLICK
	// useEffect(() => {
	//   const handleClickOutside = (event) => {
	//     if (!menuRef.current) return;

	//     if (!menuRef.current.contains(event.target)) {
	//       setOpenMenu(null);
	//     }
	//   };

	//   document.addEventListener("mousedown", handleClickOutside);
	//   return () => document.removeEventListener("mousedown", handleClickOutside);
	// }, []);

	//  HANDLERS
	const handleSubmitCandidate = async (jobUID) => {
		try {
			const res = await getJobOpeningLimit(jobUID);

			if (res?.data?.canSubmit) {
				navigate(`/Dashboard/Submit-Candidates/${jobUID}`);
			} else {
				setAlert({
					type: "fail",
					message:
						res?.data?.message ||
						"Opening limit reached. Can't send more candidates.",
				});
			}
		} catch (error) {
			setAlert({
				type: "fail",
				message:
					error?.response?.data?.message ||
					"Something went wrong while checking job limit",
			});
		}
	};

	return (
		<>
			<div className="w-full py-8 mt-12">
				<div className="max-w-[1240px] mx-auto">
					<div className="bg-white rounded-2xl px-6 py-6 shadow-sm">
						{/* HEADER */}
						<div className="bg-master-header text-white rounded-xl px-3 py-2 mb-6">
							<h3 className="font-bold text-lg">Live Jobs</h3>
						</div>

						{/* SEARCH */}
						<div className="mb-6 flex justify-between items-center flex-wrap gap-4">
							<input
								type="text"
								placeholder="Search jobs..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="border border-gray-300 rounded-full px-5 py-2 w-full max-w-sm focus:outline-none focus:ring-2 focus:ring-primary"
							/>

							<button
								onClick={() => navigate("/Dashboard/Post-Job")}
								className="bg-primary text-white rounded-full px-6 py-2 font-semibold"
							>
								+ Create Job
							</button>
						</div>

						{/* TABLE */}
						<div className="border rounded-lg overflow-visible">
							<table className="w-full text-center">
								<thead className="bg-[#f5f8ff] text-sm uppercase">
									<tr>
										<th className="py-3">#</th>

										<th className="py-3">
											<div className="flex justify-center items-center gap-2">
												Role Code
												<Funnel
													size={14}
													onClick={() => setActiveFilter("jobTitle")}
													className="cursor-pointer text-primary"
												/>
											</div>
										</th>
										<th className="py-3">
											<div className="flex justify-center items-center gap-2">
												Job Title
												<Funnel
													size={14}
													onClick={() => setActiveFilter("jobTitle")}
													className="cursor-pointer text-primary"
												/>
											</div>
										</th>

										<th className="py-3">Department</th>
										<th className="py-3">Location</th>
										<th className="py-3">Grade</th>
										<th className="py-3">Candidates</th>
										<th className="py-3">Function</th>
										<th className="py-3">Action</th>
									</tr>
								</thead>

								<tbody>
									{loading ? (
										<SkeletonTheme baseColor="#e5e7eb" highlightColor="#f3f4f6">
											{[...Array(5)].map((_, rowIdx) => (
												<tr key={rowIdx} className="border-t">
													{[...Array(8)].map((__, colIdx) => (
														<td key={colIdx} className="py-4 px-3">
															<Skeleton height={20} />
														</td>
													))}
												</tr>
											))}
										</SkeletonTheme>
									) : currentJobs.length ? (
										currentJobs.map((job, idx) => (
											<tr key={job.jobUID || idx} className="border-t">
												<td className="py-3">{indexOfFirst + idx + 1}</td>
												<td
													className="text-blue-600 font-semibold cursor-pointer"
													onClick={() =>
														navigate(
															`/Dashboard/View-Job-Details/${job.jobUID}`,
														)
													}
												>
													{job.roleCode || "Role Code Missing"}
												</td>

												<td>{job.jobTitle || "Untitled Job"}</td>

												<td>{job.department || "N/A"}</td>
												<td>{job.location || "N/A"}</td>
												<td>{job.grade || "N/A"}</td>

												<td>
													<Link
														to={`/Dashboard/Sent-Candidates-For-Job/${job.jobUID}`}
														className="text-blue-600 font-semibold"
													>
														View ({job.candidateReceived || "0"})
													</Link>
												</td>

												<td>{job.function || "N/A"}</td>

												<td className="px-3 text-xl">
													<div className="relative inline-block action-menu">
														<button
															onClick={() =>
																setOpenMenu(
																	openMenu === job.jobUID ? null : job.jobUID,
																)
															}
															className="p-1 hover:bg-gray-100 rounded-full"
														>
															<MoreVertical size={20} />
														</button>

														{openMenu === job.jobUID && (
															<div className="absolute right-0 mt-2 w-44 bg-white border rounded-lg shadow-lg z-50">
																<button
																	type="button"
																	className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
																	onClick={() => {
																		setOpenMenu(null);
																		handleSubmitCandidate(job.jobUID);
																	}}
																>
																	<UserPlus size={16} />
																	Submit Candidate
																</button>

																<button
																	type="button"
																	className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
																	onClick={() => {
																		setOpenMenu(null);
																		navigate(
																			`/Dashboard/Update-Job/${job.jobUID}`,
																			{
																				state: { readOnlyMode: true },
																			},
																		);
																	}}
																>
																	<Pencil size={16} />
																	Update Job
																</button>
															</div>
														)}
													</div>
												</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan="8" className="py-6 text-red-500 text-center">
												No Live Job Found.
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>

						{/* PAGINATION */}
						<Pagination
							totalItems={filteredJobs.length}
							itemsPerPage={jobsPerPage}
							currentPage={currentPage}
							setCurrentPage={setCurrentPage}
							setItemsPerPage={setJobsPerPage}
						/>
					</div>
				</div>
			</div>

			{/* FILTER MODAL */}
			<ColumnFilterModal
				show={!!activeFilter}
				onHide={() => setActiveFilter(null)}
				title={activeFilter}
				items={filterOptions[activeFilter] || []}
				filteredItems={(filterOptions[activeFilter] || []).filter((item) =>
					item?.toLowerCase().includes(filterSearch.toLowerCase()),
				)}
				setFilteredItems={() => {}}
				selectedItems={
					Array.isArray(filters[activeFilter]) ? filters[activeFilter] : []
				}
				setSelectedItems={(values) =>
					setFilters((prev) => ({
						...prev,
						[activeFilter]: Array.isArray(values) ? values : [],
					}))
				}
				searchText={filterSearch}
				setSearchText={setFilterSearch}
			/>

			{alert && (
				<NormalAlert
					type={alert.type}
					message={alert.message}
					onClose={() => setAlert(null)}
				/>
			)}
		</>
	);
};

export default LiveJobs;
